import React, { useEffect, useRef, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useGroupCall } from '../contexts/GroupCallContext';
import { useAuth } from '../contexts/AuthContext';
import { getMediaUrl } from '../utils/mediaUrl';
import { useTranslation } from 'react-i18next';

const formatDuration = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
};

const VideoTile = ({ stream, isLocal = false, isMuted = false, isCameraOff = false, user, mirrored = false }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (el.srcObject !== stream) {
      el.srcObject = stream || null;
    }
    if (stream) {
      el.play().catch(() => {});
    }
  }, [stream]);

  const name = user?.username || user?.name || (isLocal ? 'You' : 'Member');
  const avatar = user?.profilePicture || user?.avatarUrl;

  return (
    <div className="relative w-full h-full bg-zinc-900 rounded-2xl overflow-hidden border border-zinc-800 shadow-lg flex items-center justify-center select-none group">
      {!isCameraOff && stream ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={isLocal}
          className="w-full h-full object-cover"
          style={mirrored ? { transform: 'scaleX(-1)' } : undefined}
        />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-zinc-800 via-zinc-900 to-black p-4">
          <div className="relative">
            {avatar ? (
              <img
                src={getMediaUrl(avatar)}
                alt={name}
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-zinc-700 shadow-md"
              />
            ) : (
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-tr from-indigo-500 to-purple-600 flex items-center justify-center text-white text-xl sm:text-2xl font-bold shadow-md">
                {(name || 'U').charAt(0).toUpperCase()}
              </div>
            )}
            {isMuted && (
              <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-rose-500 text-white flex items-center justify-center text-[11px] ring-2 ring-zinc-900">
                <i className="fas fa-microphone-slash" />
              </div>
            )}
          </div>
          <p className="mt-3 text-sm font-semibold text-zinc-200 truncate max-w-[140px]">{name}</p>
          <span className="text-xs text-zinc-500">{isCameraOff ? 'Camera off' : 'Audio only'}</span>
        </div>
      )}

      {/* Overlay label: Name & Mic status */}
      <div className="absolute bottom-2.5 left-2.5 right-2.5 flex items-center justify-between pointer-events-none">
        <span className="px-2 py-0.5 rounded-md bg-black/60 backdrop-blur-md text-white text-xs font-medium truncate max-w-[120px] shadow-xs flex items-center gap-1.5">
          {isMuted && <i className="fas fa-microphone-slash text-rose-400 text-[10px]" />}
          <span className="truncate">{isLocal ? 'You' : name}</span>
        </span>
      </div>
    </div>
  );
};

const GroupCallOverlay = () => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const {
    activeCall,
    incomingCall,
    isInGroupCall,
    isMinimized,
    isMuted,
    isVideoEnabled,
    cameraFacing,
    localStream,
    peers,
    audioRoute,
    leaveGroupCall,
    acceptIncomingGroupCall,
    declineIncomingGroupCall,
    toggleGroupCallMute,
    toggleGroupCallVideo,
    flipGroupCallCamera,
    setIsMinimized,
    selectAudioOutput,
  } = useGroupCall();

  const [callDurationMs, setCallDurationMs] = useState(0);

  // Timer for active call duration
  useEffect(() => {
    if (!isInGroupCall || !activeCall) {
      setCallDurationMs(0);
      return;
    }
    const started = activeCall.startedAt || Date.now();
    const timer = setInterval(() => {
      setCallDurationMs(Date.now() - started);
    }, 1000);
    return () => clearInterval(timer);
  }, [isInGroupCall, activeCall]);

  const peerList = useMemo(() => Object.values(peers), [peers]);
  const totalCount = 1 + peerList.length;

  // Grid column CSS calculation based on participant count
  const gridLayoutClass = useMemo(() => {
    if (totalCount === 1) return 'grid-cols-1 grid-rows-1';
    if (totalCount === 2) return 'grid-cols-1 sm:grid-cols-2 grid-rows-2 sm:grid-rows-1';
    if (totalCount <= 4) return 'grid-cols-2 grid-rows-2';
    return 'grid-cols-2 sm:grid-cols-3 auto-rows-fr';
  }, [totalCount]);

  // ── 1. INCOMING GROUP CALL PROMPT ───────────────────────────────────
  if (incomingCall && !isInGroupCall) {
    const isVideo = !!incomingCall.video;
    const modal = (
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/75 backdrop-blur-md animate-in fade-in duration-200 select-none"
        role="alertdialog"
        aria-label="Incoming Group Call"
      >
        <div className="w-full max-w-sm rounded-3xl bg-zinc-950 border border-zinc-800/90 p-6 flex flex-col items-center text-center shadow-2xl relative overflow-hidden">
          {/* Pulsing background glow */}
          <div className="absolute -top-12 -left-12 w-40 h-40 bg-indigo-600/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-12 -right-12 w-40 h-40 bg-purple-600/20 rounded-full blur-3xl pointer-events-none" />

          {/* Group Avatar with Pulsing Ring */}
          <div className="relative mb-4 mt-2">
            <div className="absolute -inset-2 rounded-3xl bg-gradient-to-r from-indigo-500 to-purple-600 opacity-60 blur-md animate-pulse" />
            {incomingCall.groupAvatar ? (
              <img
                src={getMediaUrl(incomingCall.groupAvatar)}
                alt={incomingCall.groupName}
                className="relative w-24 h-24 rounded-2xl object-cover border-2 border-white/20 shadow-xl"
              />
            ) : (
              <div className="relative w-24 h-24 rounded-2xl bg-gradient-to-br from-indigo-500 via-indigo-600 to-purple-600 text-white flex items-center justify-center text-3xl font-bold border-2 border-white/20 shadow-xl">
                <i className="fas fa-users" />
              </div>
            )}
          </div>

          <h3 className="text-xl font-bold text-white tracking-tight truncate max-w-xs">
            {incomingCall.groupName}
          </h3>
          <p className="text-sm text-indigo-400 font-medium mt-1 flex items-center gap-1.5 justify-center">
            <i className={`fas ${isVideo ? 'fa-video' : 'fa-phone'} text-xs`} />
            <span>{isVideo ? 'Incoming Group Video Call' : 'Incoming Group Voice Call'}</span>
          </p>
          <p className="text-xs text-zinc-400 mt-1">
            Started by <span className="font-semibold text-zinc-300">{incomingCall.caller?.username || 'A member'}</span>
          </p>

          {/* Action buttons: Decline & Join */}
          <div className="mt-8 flex items-center justify-center gap-6 w-full">
            <button
              type="button"
              onClick={declineIncomingGroupCall}
              className="flex-1 py-3.5 px-4 rounded-2xl bg-rose-600 hover:bg-rose-700 active:scale-95 text-white font-semibold text-sm transition-all flex items-center justify-center gap-2 shadow-lg shadow-rose-900/30 cursor-pointer"
            >
              <i className="fas fa-phone-slash" />
              <span>Decline</span>
            </button>
            <button
              type="button"
              onClick={() => acceptIncomingGroupCall({ video: isVideo })}
              className="flex-1 py-3.5 px-4 rounded-2xl bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white font-semibold text-sm transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-900/30 cursor-pointer animate-pulse"
            >
              <i className={`fas ${isVideo ? 'fa-video' : 'fa-phone'}`} />
              <span>Join</span>
            </button>
          </div>
        </div>
      </div>
    );
    return createPortal(modal, document.body);
  }

  // ── 2. ACTIVE GROUP CALL ────────────────────────────────────────────
  if (!isInGroupCall || !activeCall) return null;

  // Floating Minimized Pill
  if (isMinimized) {
    const minPill = (
      <div
        className="fixed bottom-20 right-4 sm:bottom-6 sm:right-6 z-[9999] flex items-center gap-3 px-4 py-2.5 bg-zinc-900/95 dark:bg-black/95 text-white rounded-full border border-zinc-700 shadow-2xl backdrop-blur-md cursor-pointer active:scale-95 transition-all select-none animate-in fade-in slide-in-from-bottom-4"
        onClick={() => setIsMinimized(false)}
      >
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse flex-shrink-0" />
        <div className="flex flex-col min-w-0 pr-1">
          <span className="text-xs font-semibold truncate max-w-[120px]">{activeCall.groupName}</span>
          <span className="text-[11px] text-zinc-400 font-mono">{formatDuration(callDurationMs)}</span>
        </div>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggleGroupCallMute();
          }}
          className={`w-7 h-7 rounded-full flex items-center justify-center text-xs transition-colors ${
            isMuted ? 'bg-rose-500 text-white' : 'bg-white/20 hover:bg-white/30 text-white'
          }`}
          title={isMuted ? 'Unmute' : 'Mute'}
        >
          <i className={`fas ${isMuted ? 'fa-microphone-slash' : 'fa-microphone'}`} />
        </button>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            leaveGroupCall();
          }}
          className="w-7 h-7 rounded-full bg-rose-600 hover:bg-rose-700 text-white flex items-center justify-center text-xs"
          title="Leave call"
        >
          <i className="fas fa-phone-slash" />
        </button>
      </div>
    );
    return createPortal(minPill, document.body);
  }

  // Full-Screen Group Video Room Overlay
  const fullOverlay = (
    <div
      className="fixed inset-0 z-[9999] bg-zinc-950 text-white flex flex-col overflow-hidden animate-in fade-in duration-200 select-none font-sans"
      role="dialog"
      aria-label={`Group call in ${activeCall.groupName}`}
    >
      {/* Top Header Bar */}
      <div className="px-4 py-3 pt-[calc(0.75rem+env(safe-area-inset-top))] flex items-center justify-between border-b border-zinc-800/80 bg-zinc-950/80 backdrop-blur-md flex-shrink-0 z-20">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => setIsMinimized(true)}
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 active:scale-90 flex items-center justify-center text-white transition-all cursor-pointer flex-shrink-0"
            title="Minimize call"
            aria-label="Minimize"
          >
            <i className="fas fa-chevron-down text-sm" />
          </button>
          <div className="min-w-0 flex-1">
            <h2 className="text-sm sm:text-base font-bold text-white truncate flex items-center gap-2">
              <span className="truncate">{activeCall.groupName}</span>
              <span className="px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-400 text-xs font-semibold flex-shrink-0">
                {totalCount} {totalCount === 1 ? 'member' : 'members'}
              </span>
            </h2>
            <p className="text-xs text-zinc-400 font-mono tabular-nums">{formatDuration(callDurationMs)}</p>
          </div>
        </div>

        {/* Header Right: Speaker / Audio Output Switcher */}
        {audioRoute.devices?.length > 1 && (
          <button
            type="button"
            onClick={() => {
              const current = audioRoute.current;
              const next = audioRoute.devices.find((d) => d.id !== current);
              if (next) selectAudioOutput(next.id);
            }}
            className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 active:scale-95 text-xs font-medium text-zinc-200 flex items-center gap-1.5 transition-colors cursor-pointer"
          >
            <i className="fas fa-volume-high text-xs" />
            <span>Speaker</span>
          </button>
        )}
      </div>

      {/* Main Video Tiles Grid */}
      <div className={`flex-1 p-2 sm:p-4 grid gap-2 sm:gap-3.5 min-h-0 overflow-y-auto ${gridLayoutClass}`}>
        {/* Local user tile */}
        <VideoTile
          stream={localStream}
          isLocal={true}
          isMuted={isMuted}
          isCameraOff={!isVideoEnabled}
          user={currentUser}
          mirrored={cameraFacing !== 'environment'}
        />

        {/* Remote participant tiles */}
        {peerList.map((peer) => (
          <VideoTile
            key={peer.socketId}
            stream={peer.stream}
            isLocal={false}
            isMuted={peer.isMuted}
            isCameraOff={peer.isCameraOff}
            user={peer.user}
            mirrored={false}
          />
        ))}
      </div>

      {/* Bottom Floating Control Bar */}
      <div className="p-3 pb-[calc(1rem+env(safe-area-inset-bottom))] bg-zinc-950/90 border-t border-zinc-800/80 backdrop-blur-md flex items-center justify-center gap-3 sm:gap-5 flex-shrink-0 z-20">
        {/* Mute Mic Toggle */}
        <button
          type="button"
          onClick={toggleGroupCallMute}
          className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center text-lg sm:text-xl active:scale-90 transition-all cursor-pointer ${
            isMuted ? 'bg-rose-500 text-white' : 'bg-white/15 hover:bg-white/25 text-white'
          }`}
          title={isMuted ? 'Unmute' : 'Mute'}
          aria-label={isMuted ? 'Unmute' : 'Mute'}
        >
          <i className={`fas ${isMuted ? 'fa-microphone-slash' : 'fa-microphone'}`} />
        </button>

        {/* Camera On / Off Toggle */}
        <button
          type="button"
          onClick={toggleGroupCallVideo}
          className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center text-lg sm:text-xl active:scale-90 transition-all cursor-pointer ${
            !isVideoEnabled ? 'bg-rose-500 text-white' : 'bg-white/15 hover:bg-white/25 text-white'
          }`}
          title={isVideoEnabled ? 'Turn camera off' : 'Turn camera on'}
          aria-label={isVideoEnabled ? 'Turn camera off' : 'Turn camera on'}
        >
          <i className={`fas ${isVideoEnabled ? 'fa-video' : 'fa-video-slash'}`} />
        </button>

        {/* Flip Camera (Front / Back) */}
        {isVideoEnabled && (
          <button
            type="button"
            onClick={flipGroupCallCamera}
            className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center text-lg sm:text-xl active:scale-90 transition-all cursor-pointer"
            title="Flip camera"
            aria-label="Flip camera"
          >
            <i className="fas fa-camera-rotate" />
          </button>
        )}

        {/* Leave Call (Red Hang Up Button) */}
        <button
          type="button"
          onClick={leaveGroupCall}
          className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-rose-600 hover:bg-rose-700 active:scale-90 text-white flex items-center justify-center text-xl shadow-lg shadow-rose-900/40 transition-all cursor-pointer"
          title="Leave group call"
          aria-label="Leave call"
        >
          <i className="fas fa-phone-slash" />
        </button>
      </div>
    </div>
  );

  return createPortal(fullOverlay, document.body);
};

export default GroupCallOverlay;
